import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Pencil } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PageLoader } from './PageLoader';
import { InlineError } from './InlineError';
import ScreenEditor from './ScreenEditor';
import { WIDGETS } from '../widgets/registry';
import type { HomeLayout } from '../lib/types';

// The Home screen: its widgets, one after another. Each widget is self-contained
// and fetches what it shows; one that is unknown here (a newer backend) says so
// rather than breaking the rest. An administrator can customise it for the
// whole workspace.
export default function Screen({ header }: { header?: ReactNode }) {
  const { authFetch, isAdmin } = useAuth();
  const [screen, setScreen] = useState<HomeLayout | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await authFetch('/layout/home');
      if (!res.ok) throw new Error('Failed to load this screen.');
      setScreen(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load this screen.');
    }
  }, [authFetch]);

  useEffect(() => { setEditing(false); load(); }, [load]);

  if (error) return <InlineError message={error} />;
  if (!screen) return <PageLoader label="Loading..." />;

  return (
    <div>
      {(header || isAdmin) && (
        <div className="flex items-center gap-4 pb-6 mb-6 border-b border-softgrey">
          <div className="flex-1 min-w-0">{header}</div>
          {isAdmin && !editing && (
            <button
              onClick={() => setEditing(true)}
              className="shrink-0 w-8 h-8 inline-flex items-center justify-center text-coolslate border border-softgrey bg-white rounded-lg hover:text-graphite hover:bg-offwhite transition-colors print:hidden"
              title="Customise this screen for everyone in the workspace"
              aria-label="Customise this screen"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {editing ? (
        <ScreenEditor
          screen={screen}
          onDone={async changed => { setEditing(false); if (changed) await load(); }}
        />
      ) : (
        <ScreenWidgets screen={screen} />
      )}
    </div>
  );
}

export function ScreenWidgets({ screen }: { screen: HomeLayout }) {
  if (screen.widgets.length === 0) {
    return <p className="text-sm text-coolslate">Nothing has been added to this screen yet.</p>;
  }
  return (
    <div className="space-y-6">
      {screen.widgets.map(spec => {
        const Widget = WIDGETS[spec.widget];
        return Widget
          ? <Widget key={spec.id} config={spec.config ?? {}} />
          : (
            <div key={spec.id} className="border border-dashed border-softgrey rounded-xl p-4 text-[13px] text-coolslate">
              This widget ({spec.widget}) is not available.
            </div>
          );
      })}
    </div>
  );
}
